import React, { useId, useMemo, useRef, useState } from 'react';
import '../css/textField.css';
import { ClearIcon } from '../../../../../assets-module/asset-configuration/icon/iconExporter/iconExporter';

export interface TextFieldProps {
  id?: string;
  name?: string;
  value?: string;
  placeholder?: string;
  label?: string;
  type?: 'text' | 'email' | 'tel' | 'password' | 'number';
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onClear?: () => void;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  assistiveText?: string;
  error?: boolean;
  errorText?: string;
  warning?: boolean;
  showClearButton?: boolean;
  maxLength?: number;
}

const formatTel = (value: string): string => {
  const digits = value.replace(/\D/g, ''); //NOSONAR
  return digits.replace(/(\d{3})(?=\d)/g, '$1 '); //NOSONAR
};

const TextField: React.FC<TextFieldProps> = ({
  id,
  name,
  value = '',
  placeholder,
  label,
  type = 'text',
  onChange,
  onFocus,
  onBlur,
  onClear,
  required = false,
  disabled = false,
  className = '',
  startAdornment,
  endAdornment,
  assistiveText,
  error = false,
  errorText,
  warning = false,
  showClearButton = true,
  maxLength,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const reactId = useId();
  const inputId = useMemo(() => id || `text-field-${reactId}`, [id, reactId]);

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    onBlur?.(e);
  };

  const emitChangeWithValue = (
    original: React.ChangeEvent<HTMLInputElement>,
    nextValue: string
  ) => {
    if (!onChange) return;

    const nextEvent = {
      ...original,
      target: {
        ...original.target,
        name: original.target.name,
        value: nextValue,
      },
      currentTarget: {
        ...original.currentTarget,
        name: original.currentTarget.name,
        value: nextValue,
      },
    } as React.ChangeEvent<HTMLInputElement>;

    onChange(nextEvent);
  };

  const handleClear = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (onClear) {
      onClear();
    } else if (onChange) {
      const fake = {
        target: { name: name ?? '', value: '' },
        currentTarget: { name: name ?? '', value: '' },
      } as unknown as React.ChangeEvent<HTMLInputElement>;
      onChange(fake);
    }

    inputRef.current?.focus();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    let newValue = inputValue;

    if (type === 'tel') {
      newValue = inputValue.replace(/\D/g, ''); //NOSONAR
      emitChangeWithValue(e, newValue);
      return;
    }

    if (type === 'email') {
      newValue = inputValue.replace(/\s/g, ''); //NOSONAR
      emitChangeWithValue(e, newValue);
      return;
    }

    onChange?.(e);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (type === 'tel') {
      const allowedKeys = [
        'Backspace',
        'Delete',
        'Tab',
        'Escape',
        'Enter',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'Home',
        'End',
      ];

      if (
        allowedKeys.includes(e.key) ||
        ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase()))
      ) {
        return;
      }

      if (!/^\d$/.test(e.key)) {
        e.preventDefault();
      }
    }

    if (type === 'email') {
      if (e.key === ' ') e.preventDefault();
    }
  };

  const displayLabel = label || placeholder;
  const hasValue = value.length > 0;
  const showFloatingLabel = isFocused || hasValue;
  const showClear = showClearButton && hasValue && isFocused && !disabled;

  const hasAsterisk = displayLabel?.includes('*');
  const labelText = hasAsterisk
    ? displayLabel?.replace(' *', '').replace('*', '')
    : displayLabel;

  const renderLabel = () => {
     /* istanbul ignore next */
    if (!displayLabel) return null;
    return (
      <>
        {labelText}
        {hasAsterisk && (
          <span className="scl--web-component-text-field-required-asterisk"> *</span>
        )}
      </>
    );
  };

  const displayValue = type === 'tel' ? formatTel(value) : value;

  return (
    <div className={`scl--web-component-text-field-container ${className}`}>
      <label
        className={`scl--web-component-text-field-input-plate ${error ? 'error' : ''} ${warning ? 'warning' : ''} ${disabled ? 'disabled' : ''} ${isFocused ? 'focused' : ''}`}
        htmlFor={inputId}
      >
        {displayLabel && (
          <span className={`scl--web-component-text-field-label ${showFloatingLabel ? 'floating' : ''}`}>
            {renderLabel()}
          </span>
        )}

        <div className="scl--web-component-text-field-input-wrapper">
          {startAdornment && (
            <div className="scl--web-component-text-field-start-adornment">
              {startAdornment}
            </div>
          )}

          <div className="scl--web-component-text-field-value-wrapper">
            <div className="scl--web-component-text-field-text-wrapper">
              <input
                ref={inputRef}
                id={inputId}
                name={name}
                type={type}
                value={displayValue}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onFocus={handleFocus}
                onBlur={handleBlur}
                required={required}
                disabled={disabled}
                autoComplete='off'
                autoCapitalize='none'
                autoCorrect='off'
                spellCheck={false}
                maxLength={maxLength}
                className="scl--web-component-text-field-input"
              />
            </div>
          </div>

          {showClear ? (
            <button
              type="button"
              className="scl--web-component-text-field-clear-button"
              onMouseDown={handleClear}
              tabIndex={-1}
              aria-label="Clear input"
            >
              <ClearIcon width={26} height={26} />
            </button>
          ) : (
            <button
              type="button"
              className="scl--web-component-text-field-clear-button hidden"
              tabIndex={-1}
              aria-label="Clear input"
              aria-hidden="true"
              disabled
            >
              <ClearIcon width={26} height={26} />
            </button>
          )}

          {endAdornment && !showClear && (
            <div className="scl--web-component-text-field-end-adornment">
              {endAdornment}
            </div>
          )}
        </div>
      </label>

      {(assistiveText || errorText) && (
        <div className={`scl--web-component-text-field-assistive ${error ? 'error' : ''}`}>
          {error && errorText ? errorText : assistiveText}
        </div>
      )}
    </div>
  );
};

export default TextField;